// =============================================================================
// SIREPPAR - Textos precargados del tramo final de las alianzas (TEP)
// =============================================================================
// La constitución de alianza la resuelve el TRIBUNAL ELECTORAL PROVINCIAL. El
// Secretario Electoral ya no dicta la resolución: eleva el expediente al Tribunal
// y el Tribunal aprueba, rechaza o aplaza. Por eso el instrumento que se registra
// es el ACTA del TEP y no la resolución del Secretario.
//
// El acta es UN SOLO ACTO que resuelve sobre TODAS las alianzas presentadas para
// la elección: un mismo instrumento puede oficializar cuatro y rechazar una.
//
// Por eso el cuerpo completo del acta NO se escribe acá: se redacta por fuera, se
// firma y se sube como archivo (el modal acepta varios). Lo que se carga en el
// campo de texto es la parte que corresponde A ESTA alianza —lo que se guarda en
// el expediente y se le manda por correo a estas agrupaciones—. Si acá fuera el
// texto completo, en el expediente de una alianza quedaría registrado el
// reconocimiento de las otras tres.
//
// Los textos son PRECARGADOS Y EDITABLES: siguen la estructura de la parte
// resolutiva real, y quien registra el acta los ajusta antes de guardar.
// =============================================================================

import { formatFechaCalendario } from './fechas';

export interface DatosTextoResolucion {
  /** Denominación de la alianza, tal como figura en el expediente. */
  denominacion: string;
  /** Denominaciones de los partidos integrantes, en el orden del expediente. */
  integrantes: string[];
  /** Fecha del acto electoral (ISO) tomada de la convocatoria, si la hay. */
  fechaActo?: string | null;
  /** Ámbito de la convocatoria: da "elecciones provinciales" / "municipales". */
  ambitoTipo?: string | null;
}

const AMBITO_ADJETIVO: Record<string, string> = {
  PROVINCIAL: 'provinciales',
  MUNICIPAL: 'municipales',
  NACIONAL: 'nacionales',
};

/**
 * "para participar en las elecciones provinciales del 26/10/2025". Cuando el
 * expediente no tiene convocatoria asociada quedan los guiones bajos para que se
 * complete a mano: es preferible un hueco visible a una fecha inventada.
 *
 * La fecha del acto es una FECHA DE CALENDARIO: va sin conversión de huso. Esto
 * termina en el cuerpo de un acta firmada, así que un día de corrimiento acá es un
 * error en el instrumento, no un detalle de pantalla.
 */
function fraseEleccion(datos: DatosTextoResolucion): string {
  const adjetivo = AMBITO_ADJETIVO[(datos.ambitoTipo || '').toUpperCase()] || 'provinciales';
  const fecha = formatFechaCalendario(datos.fechaActo, 'corta', '__/__/____');
  return `para participar en las elecciones ${adjetivo} del ${fecha}`;
}

/** Lista en viñetas de los integrantes (o un hueco a completar si no hay ninguno). */
function listaIntegrantes(integrantes: string[]): string {
  const limpios = integrantes.map((i) => (i || '').trim()).filter(Boolean);
  if (limpios.length === 0) return '- [completar los partidos integrantes]';
  return limpios.map((i) => `- ${i.toUpperCase()}`).join('\n');
}

// =============================================================================
// Informe con el que el Secretario eleva el expediente al TEP
// =============================================================================

export interface DatosInformeTepAlianza {
  /** N° de expediente, tal como figura en la carátula. */
  numero?: string;
  /** Denominación de la alianza. */
  denominacion: string;
}

/**
 * Cuerpo del informe de elevación al Tribunal.
 *
 * Es CORTO A PROPÓSITO y deja el hueco del medio para escribir, igual que el de
 * reempadronamiento (ver `textoInformeTepReemp`): lo que el Tribunal tiene que
 * resolver se redacta caso por caso, y un párrafo genérico que afirmara que la
 * solicitud está en condiciones diría justo lo contrario en los expedientes que se
 * elevan con observaciones sin responder.
 *
 * El detalle puntual se adjunta a mano: al tildar observaciones en el panel
 * lateral del modal se arma el bloque de "Antecedentes" al final del cuerpo, con
 * el estado de cada una.
 */
export function textoInformeTepAlianza(datos: DatosInformeTepAlianza): string {
  return (
    `Se eleva al Tribunal Electoral Provincial (TEP) el expediente N° ${datos.numero || ''}, ` +
    `correspondiente a la solicitud de **constitución de la alianza electoral ` +
    `"${datos.denominacion}"** (Arts. 26 y 27, Ley XII N° 23).\n\n` +
    `------------` +
    `[Detallar acá las cuestiones que motivan la elevación y sobre las que se solicita que el ` +
    `Tribunal resuelva.` +
    `----------------]\n\n` +
    `En virtud de lo expuesto, se remiten las actuaciones para su resolución conforme a derecho.`
  );
}

// =============================================================================
// Partes resolutivas del acta del TEP (precargadas y editables)
// =============================================================================
// Cada una es lo que se le manda a las agrupaciones por correo si se tilda el
// aviso al pie del modal, así que están escritas para leerse solas, sin el
// expediente delante.

/** El TEP aprueba: la alianza queda oficializada y pasa a estado vigente. */
export function textoTepApruebaAlianza(datos: DatosTextoResolucion): string {
  return [
    'El Tribunal Electoral Provincial resuelve:',
    '',
    `OFICIALIZAR la alianza electoral "${datos.denominacion}" ${fraseEleccion(datos)}, integrada por los partidos:`,
    '',
    listaIntegrantes(datos.integrantes),
    '',
    'La alianza reconocida tiene carácter temporal y se rige por sus acuerdos de funcionamiento y ' +
      'financiero, disolviéndose automáticamente tras la finalización del proceso electoral para el ' +
      'cual fue constituida, conforme al artículo 6° de la Ley XII N° 23.',
    '',
    'Se inscribe la alianza reconocida en el Registro correspondiente, conforme al artículo 21 de la ' +
      'Ley XII N° 23.',
  ].join('\n');
}

/**
 * El TEP rechaza. El motivo es obligatorio en el modal: sin los requisitos del
 * Art. 27 que se tuvieron por incumplidos, la parte resolutiva no dice nada que
 * las agrupaciones puedan discutir.
 *
 * El rechazo NO cambia el estado de la alianza en el registro: declarar su
 * caducidad es un acto aparte, con su propio expediente.
 */
export function textoTepRechazaAlianza(datos: DatosTextoResolucion): string {
  return [
    'El Tribunal Electoral Provincial resuelve:',
    '',
    `NO HACER LUGAR a la solicitud de reconocimiento de la alianza electoral "${datos.denominacion}", ` +
      `presentada ${fraseEleccion(datos)}, integrada por los partidos:`,
    '',
    listaIntegrantes(datos.integrantes),
    '',
    'Motivo: [detallar los requisitos del artículo 27 de la Ley XII N° 23 que no se encuentran ' +
      'cumplidos].',
  ].join('\n');
}

/**
 * El TEP aplaza. El expediente vuelve a análisis y el plazo NO arranca con el acta:
 * corre desde la observación que la Secretaría curse con lo que el Tribunal
 * determinó. Decirlo en el texto evita que las agrupaciones cuenten el plazo desde
 * un acta que todavía no se les notificó en detalle.
 */
export function textoTepAplazaAlianza(datos: DatosTextoResolucion): string {
  return [
    'El Tribunal Electoral Provincial resuelve:',
    '',
    `APLAZAR la resolución sobre la constitución de la alianza electoral "${datos.denominacion}" y ` +
      'otorgar un plazo para subsanar lo señalado.',
    '',
    'El plazo comenzará a correr una vez que la Secretaría Electoral le curse la observación con lo ' +
      'que el Tribunal determinó.',
  ].join('\n');
}
